import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";

const CATEGORIES = [
    { key: "all", label: "All Work" },
    { key: "portraits", label: "Portraits" },
    { key: "gatherings", label: "Gatherings" },
    { key: "cars", label: "Cars" },
];

// Photos shown when admin hasn't uploaded any yet (kept empty so the portfolio is fully yours).
const DEMO_PHOTOS = [];

export default function Portfolio() {
    const [filter, setFilter] = useState("all");
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/photos")
            .then((res) => {
                if (cancelled) return;
                const apiPhotos = (res.data || []).map((p) => ({
                    id: p.id,
                    title: p.title || "Untitled",
                    category: p.category,
                    image_url: p.image_base64,
                }));
                setPhotos(apiPhotos);
            })
            .catch(() => setPhotos([]))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, []);

    const visible = useMemo(() => {
        if (filter === "all") return photos;
        return photos.filter((p) => p.category === filter);
    }, [photos, filter]);

    return (
        <div data-testid="portfolio-page" className="bg-[#F9F8F6]">
            {/* Header */}
            <section className="border-b border-[#E5DED5]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 md:pb-16">
                    <p className="overline" data-testid="portfolio-eyebrow">Selected Work · 2022–2026</p>
                    <h1
                        data-testid="portfolio-headline"
                        className="font-serif font-light text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight mt-6"
                    >
                        Portfolio.
                    </h1>
                    <p className="mt-6 max-w-2xl text-[#5C5853] text-base md:text-lg leading-relaxed">
                        A rolling edit of recent work — portraits, gatherings, and automotive. Click any frame to see it larger.
                    </p>

                    {/* Filters */}
                    <div
                        data-testid="portfolio-filters"
                        className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-[#E5DED5] pt-6"
                    >
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.key}
                                data-testid={`portfolio-filter-${c.key}`}
                                onClick={() => setFilter(c.key)}
                                className={`filter-btn ${filter === c.key ? "active" : ""}`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
                {loading ? (
                    <p data-testid="portfolio-loading" className="overline">Loading frames…</p>
                ) : visible.length === 0 ? (
                    <div data-testid="portfolio-empty" className="border border-dashed border-[#E5DED5] p-16 md:p-24 text-center">
                        <p className="overline">Coming soon</p>
                        <p className="font-serif text-3xl md:text-4xl tracking-tight mt-3">No frames here yet.</p>
                        <p className="text-sm text-[#5C5853] mt-3 max-w-md mx-auto">
                            New work for this category is in the darkroom — check back shortly, or head over to the contact page to commission a session.
                        </p>
                    </div>
                ) : (
                    <div className="masonry" data-testid="portfolio-grid">
                        {visible.map((p) => (
                            <div
                                key={p.id}
                                className="masonry-item"
                                data-testid={`portfolio-item-${p.id}`}
                                onClick={() => setLightbox(p)}
                            >
                                <img src={p.image_url} alt={p.title} loading="lazy" />
                                <div className="flex items-center justify-between mt-3">
                                    <p className="font-serif text-lg leading-none">{p.title}</p>
                                    <p className="overline">{p.category}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Lightbox */}
            {lightbox && (
                <div
                    data-testid="portfolio-lightbox"
                    className="fixed inset-0 z-[60] bg-[#1A1817]/95 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setLightbox(null)}
                >
                    <button
                        data-testid="portfolio-lightbox-close"
                        className="absolute top-6 right-6 text-[#F9F8F6] hover:text-[#A0522D] transition-colors"
                        onClick={() => setLightbox(null)}
                    >
                        <X className="w-7 h-7" />
                    </button>
                    <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={lightbox.image_url}
                            alt={lightbox.title}
                            className="w-full h-auto max-h-[80vh] object-contain"
                        />
                        <div className="flex items-center justify-between mt-6 text-[#F9F8F6]">
                            <p className="font-serif text-2xl">{lightbox.title}</p>
                            <p className="overline">{lightbox.category}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
